import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const ApiStatusContext = createContext(null);

const FAILURE_THRESHOLD = 3;
const DEBOUNCE_MS = 1000;
const HEALTH_CHECK_INTERVAL_MS = 10000;
const HEALTH_CHECK_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3030') + '/health';

export function ApiStatusProvider({ children }) {
  const [apiUnavailable, setApiUnavailable] = useState(false);
  const failureCount = useRef(0);
  const lastFailureTime = useRef(0);

  const reportApiFailure = useCallback(() => {
    const now = Date.now();
    if (now - lastFailureTime.current < DEBOUNCE_MS) {
      return;
    }
    lastFailureTime.current = now;

    failureCount.current += 1;
    if (failureCount.current >= FAILURE_THRESHOLD) {
      setApiUnavailable(true);
    }
  }, []);

  const reportApiSuccess = useCallback(() => {
    failureCount.current = 0;
    setApiUnavailable(false);
  }, []);

  useEffect(() => {
    const handleFailure = () => reportApiFailure();
    const handleSuccess = () => reportApiSuccess();

    window.addEventListener('api:failure', handleFailure);
    window.addEventListener('api:success', handleSuccess);

    return () => {
      window.removeEventListener('api:failure', handleFailure);
      window.removeEventListener('api:success', handleSuccess);
    };
  }, [reportApiFailure, reportApiSuccess]);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await fetch(HEALTH_CHECK_URL, { method: 'GET', signal: AbortSignal.timeout(5000) });
        if (response.ok) {
          window.dispatchEvent(new Event('api:success'));
        } else {
          window.dispatchEvent(new Event('api:failure'));
        }
      } catch {
        window.dispatchEvent(new Event('api:failure'));
      }
    };

    const intervalId = setInterval(checkHealth, HEALTH_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <ApiStatusContext.Provider value={{ apiUnavailable, reportApiFailure, reportApiSuccess }}>
      {children}
    </ApiStatusContext.Provider>
  );
}

export function useApiStatus() {
  const context = useContext(ApiStatusContext);
  if (!context) {
    throw new Error('useApiStatus must be used within ApiStatusProvider');
  }
  return context;
}

export { FAILURE_THRESHOLD, DEBOUNCE_MS, HEALTH_CHECK_INTERVAL_MS, HEALTH_CHECK_URL };
